import { useCallback, useEffect, useRef } from 'react';
import { getDuration, useReliableInterval } from '@common-fe/common-fe';

import useAuthorization from '@/modules/user/hooks/useAuthorization';
import Token from '@/utils/handlers/Token';

const LOGOUT_TIMESTAMP_KEY = 'logout-timestamp';
const IMMEDIATE_LOGOUT_EVENT = 'immediate-logout-event';
const LOGOUT_DELAY = getDuration({ seconds: 10 });

interface Props {
  isLoggedIn?: boolean;
}

const useLogoutOnTabClose = ({ isLoggedIn }: Props) => {
  const { handleLogout } = useAuthorization();
  const logoutTimeout = useRef(0);

  const { start: startReliableInterval,  stop: stopReliableInterval} = useReliableInterval(() => {
    logoutTimeout.current = logoutTimeout.current - 1;
    const hasTimer = localStorage.getItem(LOGOUT_TIMESTAMP_KEY);
    if (!hasTimer) {
      stopReliableInterval();
      return;
    }
    if (logoutTimeout.current <= 0) {
      stopReliableInterval();
      handleLogout();
    }
  }, getDuration({ seconds: 1 }));

  const syncLogout = useCallback((event: StorageEvent) => {
    if (!isLoggedIn) {
      return;
    }

    if (!Token.getSSODataFromHash() && event.key === IMMEDIATE_LOGOUT_EVENT) {
      handleLogout();
      return;
    }

    if (event.key === LOGOUT_TIMESTAMP_KEY) {
      if (event.newValue) {
        const timestamp = parseInt(event.newValue, 10);
        const currentTime = Date.now();
        const timeRemaining = Math.max(0, Math.ceil((LOGOUT_DELAY - (currentTime - timestamp)) / 1000));
        logoutTimeout.current = timeRemaining;
        if (timeRemaining > 0) {
          startReliableInterval();
        } else {
          handleLogout();
        }
      }
    }
  }, [isLoggedIn, startReliableInterval, handleLogout]);

  const setLogoutTimer = useCallback(() => {
    localStorage.setItem(LOGOUT_TIMESTAMP_KEY, Date.now().toString());
  }, []);

  const checkLogoutRequired = useCallback(() => {
    const ssoData = Token.getSSODataFromHash();
    if (ssoData) {
      localStorage.setItem(IMMEDIATE_LOGOUT_EVENT, 'true');
      handleLogout(() => Token.setSSOToken(ssoData?.accessToken, ssoData?.expiresIn));
      return;
    }

    const logoutTimer = localStorage.getItem(LOGOUT_TIMESTAMP_KEY);
    if (logoutTimer) {
      const logoutTime = parseInt(logoutTimer, 10);
      const timeSinceLogout = Date.now() - logoutTime;
      if (timeSinceLogout >= LOGOUT_DELAY) {
        handleLogout();
      } else {
        localStorage.removeItem(LOGOUT_TIMESTAMP_KEY);
      }
    }
  }, [handleLogout]);

  useEffect(() => {
    if (!isLoggedIn) {
      localStorage.removeItem(LOGOUT_TIMESTAMP_KEY);
      return;
    };
    checkLogoutRequired();
    window.addEventListener('beforeunload', setLogoutTimer);
    window.addEventListener('storage', syncLogout);
    return () => {
      window.removeEventListener('beforeunload', setLogoutTimer);
      window.removeEventListener('storage', syncLogout);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);
};

export default useLogoutOnTabClose;
